import { useEffect, useState, type RefObject } from 'react';

/**
 * Gutter the rail needs on either side of the transcript: 12rem of clearance
 * for an 11rem rail, so it never reaches the messages. Required on both sides
 * because the column is centered — the space it takes on the right is space the
 * conversation has already given up on the left.
 */
export const RAIL_GUTTER_PX = 192;

/**
 * Whether a pane has room for the turn rail beside a column of this width.
 *
 * Widening the column eats the gutter the rail lives in, so this is asked of
 * the column as drawn rather than of the 48rem it used to always be.
 */
export function hasRoomForRail(paneWidth: number, columnWidth: number): boolean {
  return paneWidth >= columnWidth + RAIL_GUTTER_PX * 2;
}

/**
 * Whether the chat pane is wide enough for the turn rail beside its column.
 *
 * Measured, not matched against the viewport: the chat shares the window with
 * the agent sidebar and the detail panel, so the pane is routinely much
 * narrower than the screen. Measuring the column too, rather than reading the
 * width preference, keeps the answer true on a surface that renders the
 * default column without publishing a width.
 *
 * Only the derived boolean is state, so a window drag or a column drag renders
 * the transcript on the one frame the answer flips rather than on every pixel.
 * Answering `false` keeps the rail unmounted rather than merely hidden — on a
 * phone there is then no nav in the DOM, no IntersectionObserver over the
 * transcript's rows, and nothing to pay for.
 */
export function useHasRoomForRail(
  paneRef: RefObject<HTMLElement | null>,
  columnRef: RefObject<HTMLElement | null>,
): boolean {
  const [hasRoom, setHasRoom] = useState(false);

  useEffect(() => {
    const pane = paneRef.current;
    const column = columnRef.current;
    if (!pane || !column) return;

    const measure = () => setHasRoom(hasRoomForRail(pane.clientWidth, column.clientWidth));
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    observer.observe(column);
    return () => observer.disconnect();
  }, [paneRef, columnRef]);

  return hasRoom;
}

/** Ignore the last pixel or two of overflow: a scrollbar that short is not a journey. */
const SCROLLABLE_SLACK_PX = 8;

/**
 * Whether a container has anything to scroll.
 *
 * The rail uses it to decide whether an end-of-chat jump is worth a row: when
 * the whole conversation is already on screen, "go to the end" goes nowhere,
 * and a control that does nothing is worse than no control.
 *
 * Watches the content as well as the container, since the transcript grows
 * under a fixed-height parent as the agent streams.
 */
export function useIsScrollable(ref: RefObject<HTMLElement | null>): boolean {
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () =>
      setScrollable(element.scrollHeight > element.clientHeight + SCROLLABLE_SLACK_PX);
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    const content = element.firstElementChild;
    if (content) observer.observe(content);
    return () => observer.disconnect();
  }, [ref]);

  return scrollable;
}
